import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, api, useTenant } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import TenantFilter from '../components/TenantFilter';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Building2, 
  Users, 
  Loader2, 
  Download, 
  Receipt, 
  AlertCircle,
  Filter,
  RefreshCw,
  Wallet,
  ArrowUpRight,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';

const BillingPage = () => {
  const { token, hasRole } = useAuth();
  const { selectedTenant, getTenantParam } = useTenant();
  const [stats, setStats] = useState(null);
  const [approvedMissions, setApprovedMissions] = useState([]);
  const [allMissions, setAllMissions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoicing, setInvoicing] = useState(false);
  
  // Filtros
  const [daysFilter, setDaysFilter] = useState(90);
  const [customerFilter, setCustomerFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, [token, selectedTenant]);

  const fetchData = async () => {
    setLoading(true);
    const tenantParam = getTenantParam();
    try {
      const [billingStats, approved, missions, customersData, suppliersData] = await Promise.all([
        api.get(`/billing/stats${tenantParam}`, token),
        api.get(`/billing/approved-missions${tenantParam}`, token),
        api.get(`/missions${tenantParam}`, token),
        api.get(`/customers${tenantParam}`, token),
        api.get(`/suppliers${tenantParam}`, token)
      ]);
      setStats(billingStats);
      setApprovedMissions(approved);
      setAllMissions(missions);
      setCustomers(customersData);
      setSuppliers(suppliersData);
    } catch (error) {
      toast.error('Erro ao carregar dados de faturamento');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar missões aprovadas
  const filteredApproved = useMemo(() => {
    return approvedMissions.filter(m => {
      if (customerFilter !== 'all' && m.customer_id !== customerFilter) return false;
      if (supplierFilter !== 'all' && m.supplier_id !== supplierFilter) return false;
      return true;
    });
  }, [approvedMissions, customerFilter, supplierFilter]);

  // Calcular métricas de faturamento
  const billingMetrics = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysFilter);
    
    let filtered = allMissions.filter(m => new Date(m.created_at) >= cutoff);
    if (customerFilter !== 'all') filtered = filtered.filter(m => m.customer_id === customerFilter);
    if (supplierFilter !== 'all') filtered = filtered.filter(m => m.supplier_id === supplierFilter);

    // Custos e receitas por mês
    const byMonth = {};
    filtered.forEach(m => {
      const month = new Date(m.created_at).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      if (!byMonth[month]) byMonth[month] = { month, custo: 0, receita: 0, margem: 0, count: 0 };
      byMonth[month].custo += m.supplier_cost || 500;
      byMonth[month].receita += m.customer_revenue || 750;
      byMonth[month].margem += (m.customer_revenue || 750) - (m.supplier_cost || 500);
      byMonth[month].count++;
    });
    const monthlyData = Object.values(byMonth).slice(-6);

    // Por cliente
    const byCustomer = {};
    filtered.forEach(m => {
      const customer = m.customer_name || 'Não informado';
      if (!byCustomer[customer]) byCustomer[customer] = { customer, receita: 0, count: 0 };
      byCustomer[customer].receita += m.customer_revenue || 750;
      byCustomer[customer].count++;
    });
    const customerData = Object.values(byCustomer).sort((a, b) => b.receita - a.receita).slice(0, 6);

    // Por fornecedor
    const bySupplier = {};
    filtered.forEach(m => {
      const supplier = m.supplier_name || 'Não atribuído';
      if (!bySupplier[supplier]) bySupplier[supplier] = { supplier, custo: 0, count: 0 };
      bySupplier[supplier].custo += m.supplier_cost || 500;
      bySupplier[supplier].count++;
    });
    const supplierData = Object.values(bySupplier).sort((a, b) => b.custo - a.custo).slice(0, 6);

    // Totais
    const totalReceita = filtered.reduce((sum, m) => sum + (m.customer_revenue || 750), 0);
    const totalCusto = filtered.reduce((sum, m) => sum + (m.supplier_cost || 500), 0);
    const totalMargem = totalReceita - totalCusto;
    const margemPercent = totalReceita > 0 ? Math.round((totalMargem / totalReceita) * 100) : 0;

    return { monthlyData, customerData, supplierData, totalReceita, totalCusto, totalMargem, margemPercent };
  }, [allMissions, daysFilter, customerFilter, supplierFilter]);

  const handleInvoice = async (missionId) => {
    setInvoicing(true);
    const tenantParam = getTenantParam();
    try {
      await api.post(`/missions/${missionId}/invoice${tenantParam}`, {}, token);
      toast.success('Missão faturada com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao faturar missão');
    } finally {
      setInvoicing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const exportToCSV = async () => {
    const tenantParam = getTenantParam();
    try {
      toast.info('Gerando relatório...');
      const exportData = await api.get(`/billing/export${tenantParam}`, token);
      
      // Criar CSV com todos os campos detalhados
      const headers = [
        'Código',
        'Data Criação',
        'Cliente',
        'Fornecedor',
        'Motivo',
        'Placa',
        'Status',
        'Km Total',
        'Tempo (min)',
        'Fornec. Valor Base',
        'Fornec. Km Excedente',
        'Fornec. Km Excedente (R$)',
        'Fornec. Tempo Excedente (min)',
        'Fornec. Tempo Excedente (R$)',
        'Fornec. Custo Total',
        'Cliente Valor Base',
        'Cliente Km Excedente',
        'Cliente Km Excedente (R$)',
        'Cliente Tempo Excedente (min)',
        'Cliente Tempo Excedente (R$)',
        'Cliente Receita Total',
        'Lucro'
      ];
      
      const rows = exportData.missions.map(m => [
        m.codigo,
        m.data_criacao,
        m.cliente,
        m.fornecedor,
        m.motivo,
        m.placa,
        m.status,
        m.km_total,
        m.tempo_total_minutos,
        m.fornecedor_valor_base,
        m.fornecedor_km_excedente,
        m.fornecedor_km_excedente_valor,
        m.fornecedor_tempo_excedente_min,
        m.fornecedor_tempo_excedente_valor,
        m.fornecedor_custo_total,
        m.cliente_valor_base,
        m.cliente_km_excedente,
        m.cliente_km_excedente_valor,
        m.cliente_tempo_excedente_min,
        m.cliente_tempo_excedente_valor,
        m.cliente_receita_total,
        m.lucro
      ].join(','));
      
      // Adicionar totais
      rows.push('');
      rows.push(`TOTAIS,,,,,,,${exportData.totals.total_km},,,,,,,,${exportData.totals.total_custo_fornecedor},,,,,,${exportData.totals.total_receita_cliente},${exportData.totals.total_lucro}`);
      
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `faturamento_detalhado_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success(`Relatório exportado com ${exportData.missions.length} missões`);
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3">
          <p className="font-medium text-slate-900 text-sm mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm font-mono">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Faturamento
          </h1>
          <p className="text-slate-500 mt-1">Gestão financeira de missões</p>
        </div>
        <div className="flex items-center gap-2">
          <TenantFilter />
          <Button variant="outline" onClick={fetchData} className="rounded-xl">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={exportToCSV} className="bg-slate-900 hover:bg-slate-800 rounded-xl" data-testid="export-billing-btn">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="premium-card border-0">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-4 py-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">Período:</span>
              <Input
                type="number"
                min="7"
                max="365"
                value={daysFilter}
                onChange={(e) => setDaysFilter(parseInt(e.target.value) || 90)}
                className="w-20 h-8 text-center rounded-lg border-slate-200"
              />
              <span className="text-sm text-slate-500">dias</span>
            </div>

            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl border-slate-200 bg-slate-50">
                <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Clientes</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-44 h-10 rounded-xl border-slate-200 bg-slate-50">
                <Users className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Fornecedor" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="all">Todos Fornecedores</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(customerFilter !== 'all' || supplierFilter !== 'all') && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setCustomerFilter('all');
                  setSupplierFilter('all');
                }}
                className="text-slate-500"
              >
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="premium-card border-0 bg-gradient-to-br from-green-600 to-green-700 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Receita Total</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(billingMetrics.totalReceita)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card border-0 bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Custo Total</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(billingMetrics.totalCusto)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <TrendingDown className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card border-0 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-300 text-sm">Margem Bruta</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                  {formatCurrency(billingMetrics.totalMargem)}
                </p>
                <Badge className="mt-1 bg-white/20 text-white text-xs">
                  {billingMetrics.margemPercent}%
                </Badge>
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <Wallet className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="premium-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Aguardando Faturar</p>
                <p className="text-2xl font-bold text-orange-600 mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                  {filteredApproved.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Franchise Control */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="premium-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Dentro da Franquia</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {stats?.missions_in_franchise || 0}
                </p>
                <p className="text-xs text-green-600 mt-1">Sem custo adicional</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="premium-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Fora da Franquia</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>
                  {stats?.missions_out_franchise || 0}
                </p>
                <p className="text-xs text-red-600 mt-1">Com valores excedentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="charts" className="space-y-4">
        <TabsList className="bg-slate-100 rounded-xl p-1">
          <TabsTrigger value="charts" className="rounded-lg" data-testid="charts-tab">Gráficos</TabsTrigger>
          <TabsTrigger value="approved" className="rounded-lg" data-testid="approved-tab">
            Faturar ({filteredApproved.length})
          </TabsTrigger>
          <TabsTrigger value="details" className="rounded-lg">Detalhamento</TabsTrigger>
        </TabsList>

        <TabsContent value="charts" className="space-y-6">
          {/* Revenue vs Cost Chart */}
          <Card className="premium-card border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                <DollarSign className="w-5 h-5 text-green-500" />
                Receita vs Custo por Mês
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={billingMetrics.monthlyData}>
                    <defs>
                      <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="receita" fill="url(#colorReceita)" stroke="#10B981" strokeWidth={2} name="Receita" />
                    <Bar dataKey="custo" fill="#EF4444" name="Custo" radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="margem" stroke="#0F172A" strokeWidth={2} dot={{ r: 4 }} name="Margem" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* By Customer and Supplier */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="premium-card border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                  <Building2 className="w-5 h-5 text-blue-500" />
                  Receita por Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={billingMetrics.customerData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                      <YAxis dataKey="customer" type="category" tick={{ fontSize: 11 }} width={90} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="receita" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Receita" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="premium-card border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                  <Users className="w-5 h-5 text-orange-500" />
                  Custo por Fornecedor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={billingMetrics.supplierData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                      <YAxis dataKey="supplier" type="category" tick={{ fontSize: 11 }} width={90} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="custo" fill="#F97316" radius={[0, 4, 4, 0]} name="Custo" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="approved">
          <Card className="premium-card border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Public Sans' }}>
                <Receipt className="w-5 h-5 text-orange-500" />
                Missões Aprovadas para Faturamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredApproved.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Nenhuma missão aguardando faturamento</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Cliente</th>
                        <th>Fornecedor</th>
                        <th className="text-right">Custo</th>
                        <th className="text-right">Receita</th>
                        <th className="text-right">Margem</th>
                        <th>Contestada</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApproved.map((mission) => (
                        <tr key={mission.id} data-testid={`approved-mission-${mission.id}`}>
                          <td className="font-mono font-medium">{mission.code}</td>
                          <td>{mission.customer_name || '-'}</td>
                          <td>{mission.supplier_name || '-'}</td>
                          <td className="font-mono text-red-600 text-right">{formatCurrency(mission.supplier_cost)}</td>
                          <td className="font-mono text-green-600 text-right">{formatCurrency(mission.customer_revenue)}</td>
                          <td className="font-mono font-medium text-right">{formatCurrency((mission.customer_revenue || 0) - (mission.supplier_cost || 0))}</td>
                          <td>
                            {mission.contested ? (
                              <Badge className="bg-red-100 text-red-700">Sim</Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600">Não</Badge>
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              onClick={() => handleInvoice(mission.id)}
                              disabled={invoicing}
                              className="bg-slate-900 hover:bg-slate-800 rounded-lg"
                              data-testid={`invoice-btn-${mission.id}`}
                            >
                              Faturar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="premium-card border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                  Resumo por Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-80">
                  <table className="data-table text-sm">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th className="text-right">Missões</th>
                        <th className="text-right">Receita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingMetrics.customerData.map((item, index) => (
                        <tr key={index}>
                          <td className="font-medium">{item.customer}</td>
                          <td className="text-right font-mono">{item.count}</td>
                          <td className="text-right font-mono text-green-600">{formatCurrency(item.receita)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="premium-card border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg" style={{ fontFamily: 'Public Sans' }}>
                  Resumo por Fornecedor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-80">
                  <table className="data-table text-sm">
                    <thead>
                      <tr>
                        <th>Fornecedor</th>
                        <th className="text-right">Missões</th>
                        <th className="text-right">Custo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingMetrics.supplierData.map((item, index) => (
                        <tr key={index}>
                          <td className="font-medium">{item.supplier}</td>
                          <td className="text-right font-mono">{item.count}</td>
                          <td className="text-right font-mono text-red-600">{formatCurrency(item.custo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingPage;
